import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';

export interface MatchResult {
  id: string;
  user1: {
    id: string;
    age?: number;
    gender?: string;
    username?: string;
    shortBio?: string;
    whoAmI?: string;
    interests?: string;
  };
  user2: {
    id: string;
    age?: number;
    gender?: string;
    username?: string;
    shortBio?: string;
    whoAmI?: string;
    interests?: string;
  };
  status?: string;
  acceptedByUser1: boolean;
  acceptedByUser2: boolean;
}

export interface SpinStartedEvent {
  wheelId: string;
  innerOffset: number;
  outerOffset: number;
}

export interface SpinCompletedEvent {
  wheelId: string;
  currentRound: number;
  lastSpinDate: string;
  matches: MatchResult[];
}

class SignalRService {
  private connection: HubConnection | null = null;
  private wheelId: string | null = null;

  public async connect(): Promise<void> {
    try {
      // Check if we're already connected
      if (this.connection && this.connection.state === 'Connected') {
        console.log('SignalR already connected');
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const url = `${process.env.REACT_APP_API_URL}hubs/wheel${user?.id ? `?userId=${user.id}` : ''}`;
      
      // If there's an existing connection, stop it first
      if (this.connection) {
        await this.connection.stop();
      }

      this.connection = new HubConnectionBuilder()
        .withUrl(url)
        .withAutomaticReconnect()
        .build();

      // Set up event handlers before starting the connection
      this.setupEventHandlers();

      await this.connection.start();
      console.log('SignalR Connected');
    } catch (err) {
      console.error('SignalR Connection Error:', err);
    }
  }

  public async joinWheel(wheelId: string): Promise<void> {
    if (!this.connection) return;
    
    try {
      this.wheelId = wheelId;
      await this.connection.invoke('JoinWheel', wheelId);
      console.log('Joined wheel group:', wheelId);
    } catch (err) {
      console.error('Error joining wheel:', err);
    }
  }

  public async leaveWheel(wheelId: string): Promise<void> {
    if (!this.connection) return;
    
    try {
      this.wheelId = null;
      await this.connection.invoke('LeaveWheel', wheelId);
      console.log('Left wheel group:', wheelId);
    } catch (err) {
      console.error('Error leaving wheel:', err);
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Wheel spin events
    this.connection.on('WheelSpinStarted', (event: SpinStartedEvent) => {
      document.dispatchEvent(new CustomEvent('wheelSpinStarted', { detail: event }));
    });

    this.connection.on('MatchesCreated', (matches: MatchResult[]) => {
      console.log('Matches created:', matches);
      document.dispatchEvent(new CustomEvent('matchesCreated', { detail: matches }));
    });

    this.connection.on('WheelSpinCompleted', (event: SpinCompletedEvent) => {
      document.dispatchEvent(new CustomEvent('wheelSpinCompleted', { detail: event }));
    });

    // Slot events
    this.connection.on('SlotTaken', (userId: string, slotId: number) => {
      document.dispatchEvent(new CustomEvent('slotTaken', { 
        detail: { userId, slotId } 
      }));
    });

    this.connection.on('SlotLeft', (userId: string, slotId: number) => {
      document.dispatchEvent(new CustomEvent('slotLeft', { 
        detail: { userId, slotId }
      }));
    });

    // User events
    this.connection.on('UserJoined', (user: { 
      id: string; 
      age?: number; 
      gender?: string; 
      isRegistered: boolean; 
      username?: string;
      shortBio?: string;
      whoAmI?: string;
      interests?: string;
    }) => {
      document.dispatchEvent(new CustomEvent('userJoined', { detail: user }));
    });

    this.connection.on('UserLeft', (userId: string) => {
      document.dispatchEvent(new CustomEvent('userLeft', { detail: userId }));
    });

    // WebRTC signaling events
    this.connection.on('OfferReceived', (matchId: string, offer: RTCSessionDescriptionInit) => {
      document.dispatchEvent(new CustomEvent('offerReceived', { 
        detail: { matchId, offer } 
      }));
    });

    this.connection.on('AnswerReceived', (matchId: string, answer: RTCSessionDescriptionInit) => {
      document.dispatchEvent(new CustomEvent('answerReceived', { 
        detail: { matchId, answer } 
      }));
    });

    this.connection.on('IceCandidateReceived', (matchId: string, candidate: RTCIceCandidateInit) => {
      document.dispatchEvent(new CustomEvent('iceCandidateReceived', { 
        detail: { matchId, candidate } 
      }));
    });

    this.connection.on('MatchAccepted', (matchId: string, userId: string, acceptedByUser1: boolean, acceptedByUser2: boolean) => {
      console.log('Match accepted:', matchId, 'by user:', userId, 'User1:', acceptedByUser1, 'User2:', acceptedByUser2);
      document.dispatchEvent(new CustomEvent('matchAccepted', { 
        detail: { matchId, userId, acceptedByUser1, acceptedByUser2 } 
      }));
    });

    this.connection.on('MatchDeclined', (matchId: string) => {
      console.log('Match declined:', matchId);
      document.dispatchEvent(new CustomEvent('matchDeclined', { 
        detail: { matchId } 
      }));
    });
  }

  public disconnect(): void {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
  }

  public async takeSlot(wheelId: string, userId: string, slotId: number): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('TakeSlot', wheelId, userId, slotId);
    } catch (err) {
      console.error('Error taking slot:', err);
    }
  }

  public async sendOffer(matchId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('SendOffer', matchId, offer);
    } catch (err) {
      console.error('Error sending offer:', err);
    }
  }

  public async sendAnswer(matchId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('SendAnswer', matchId, answer);
    } catch (err) {
      console.error('Error sending answer:', err);
    }
  }

  public async sendIceCandidate(matchId: string, candidate: RTCIceCandidate): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('SendIceCandidate', matchId, candidate);
    } catch (err) {
      console.error('Error sending ICE candidate:', err);
    }
  }

  public async acceptMatch(matchId: string): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('AcceptMatch', matchId);
      console.log('Match accepted:', matchId);
    } catch (err) {
      console.error('Error accepting match:', err);
      throw err;
    }
  }

  public async declineMatch(matchId: string): Promise<void> {
    if (!this.connection) return;
    
    try {
      await this.connection.invoke('DeclineMatch', matchId);
      console.log('Match declined:', matchId);
    } catch (err) {
      console.error('Error declining match:', err);
      throw err;
    }
  }

  public getConnectionId(): string | undefined {
    return this.connection?.connectionId;
  }
}

export const signalRService = new SignalRService(); 